import { Feature, Point } from 'geojson';

import { SnMapClusterPoint } from '@lib/api';
import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';

import { EsriModules } from '../../tokens/esri.tokens';
import { FsAdvancedEsriMarker } from './advanced-esri-marker';
import { IAdvancedEsriMarkerOptions } from './advanced-marker-options';

export interface IAdvancedEsriFeatureOptions extends IAdvancedEsriMarkerOptions {
  // Additional feature-specific properties
  entityType?: string;
  entityId?: string;
  priority?: 'high' | 'normal' | 'low';
  category?: string;
  tags?: string[];
  customData?: Record<string, unknown>;
}

export class FsAdvancedEsriFeatureMarker extends FsAdvancedEsriMarker {
  public updated: number = Date.now();
  public feature: Feature<Point>;
  public cluster: SnMapClusterPoint | undefined;

  // Feature-specific properties
  private _distanceToDestination: number = 0;

  private _featureOptions: IAdvancedEsriFeatureOptions;

  constructor(esri: EsriModules, options?: IAdvancedEsriFeatureOptions) {
    super(esri, {
      position: options?.position,
      title: options?.title,
      draggable: options?.draggable,
      zIndex: options?.zIndex,
      map: options?.map,
      ...options,
    });

    this._featureOptions = {
      size: '24px',
      color: options?.icon?.color || '#fff',
      rotation: options?.rotation || 0,
      outline: this.outline || false,
      ...options,
    };

    // Initialize feature-specific properties
    this.feature = this._featureOptions.feature;
    this.cluster = this._featureOptions.cluster;

    // Handle cluster markers for Axon layers
    if (this.cluster && this._featureOptions.isAxonLayer) {
      this._setupClusterMarker();
      return;
    }

    // Handle image markers
    if (this._isImageIcon(this.icon)) {
      this._setupImageMarker();
      return;
    }

    // Handle font icon markers
    if (this._isFontIcon(this.icon)) {
      this._setupFontIconMarker();
      return;
    }

    // Default marker setup
    this._setupDefaultMarker();
  }

  // Enhanced animation methods for feature markers
  public setDestination(destination: SnLonLatLike): void {
    super.setDestination(destination);
    // Reset cached distance so it gets recalculated on next call
    this._distanceToDestination = 0;
  }

  public getDestination(): SnLonLat | undefined {
    const animationState = this.getAnimationState();
    return animationState.destination;
  }

  public getDistanceToDestination(): number {
    // Cache the distance calculation result
    if (this._distanceToDestination === 0) {
      const destination = this.getDestination();
      if (!destination) return 0;

      const current = this.getLonLat();
      if (!current) return 0;

      // Simple distance calculation for testing
      // In production, this would delegate to the adapter's geometry service
      const R = 6371; // Earth's radius in kilometers
      const dLat = ((destination.lat - current.lat) * Math.PI) / 180;
      const dLon = ((destination.lon - current.lon) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((current.lat * Math.PI) / 180) *
          Math.cos((destination.lat * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      this._distanceToDestination = R * c;
    }

    return this._distanceToDestination;
  }

  public setFollowMode(followMode: boolean): void {
    super.setFollowMode(followMode);
  }

  public getFollowMode(): boolean {
    return super.getFollowMode();
  }

  // Enhanced movement with smooth animation
  public animateToDestination(durationSeconds: number): void {
    const destination = this.getDestination();
    if (!destination) return;

    this.animateSetLonLat(durationSeconds, destination);
  }

  // Feature-specific methods
  public getEntityType(): string | undefined {
    return this._featureOptions.entityType;
  }

  public setEntityType(entityType: string): void {
    this._featureOptions.entityType = entityType;
  }

  public getEntityId(): string | undefined {
    return this._featureOptions.entityId;
  }

  public setEntityId(entityId: string): void {
    this._featureOptions.entityId = entityId;
  }

  public getPriority(): 'high' | 'normal' | 'low' {
    return this._featureOptions.priority || 'normal';
  }

  public setPriority(priority: 'high' | 'normal' | 'low'): void {
    this._featureOptions.priority = priority;
    this._updateZIndexByPriority();
  }

  public getCategory(): string | undefined {
    return this._featureOptions.category;
  }

  public setCategory(category: string): void {
    this._featureOptions.category = category;
  }

  public getTags(): string[] {
    return this._featureOptions.tags || [];
  }

  public setTags(tags: string[]): void {
    this._featureOptions.tags = tags;
  }

  public addTag(tag: string): void {
    if (!this._featureOptions.tags) {
      this._featureOptions.tags = [];
    }
    if (!this._featureOptions.tags.includes(tag)) {
      this._featureOptions.tags.push(tag);
    }
  }

  public removeTag(tag: string): void {
    if (this._featureOptions.tags) {
      this._featureOptions.tags = this._featureOptions.tags.filter((t) => t !== tag);
    }
  }

  public getCustomData(): Record<string, unknown> | undefined {
    return this._featureOptions.customData;
  }

  public setCustomData(data: Record<string, unknown>): void {
    this._featureOptions.customData = data;
  }

  // Cluster-specific methods
  public isCluster(): boolean {
    return !!this.cluster;
  }

  public getClusterCount(): number {
    return this.cluster?.properties?.objects || 0;
  }

  // Private helper methods
  private _setupClusterMarker(): void {
    this.content.innerHTML = `<span class="fs ab3"></span>`;
    this.content.classList.add('fs__advanced-axon-cluster-marker');
  }

  private _setupImageMarker(): void {
    if (this._isImageIcon(this.icon)) {
      this.content.innerHTML = `<img src="${this.icon.url}" />`;
      this.content.classList.add('fs__center-marker');
      this.content.classList.add('fs__advanced-marker');
      this.content.classList.add('fs__advanced-feature-image-marker');
    }
  }

  private _setupFontIconMarker(): void {
    if (this._isFontIcon(this.icon)) {
      const iconClass = `${this.icon.fontSet} ${this.icon.fontIcon}`;
      this.content.innerHTML = `<i class="${iconClass}" style="color: ${this.color};"></i>`;
      this.content.classList.add('fs__center-marker');
      this.content.classList.add('fs__advanced-marker');
      this.content.classList.add('fs__advanced-feature-marker');

      if (this.iconLabel) {
        this._addIconLabel();
      }
    }
  }

  private _setupDefaultMarker(): void {
    this.content.innerHTML = `<i class="fa-solid fa-map-pin" style="color: ${this.color};"></i>`;
    this.content.classList.add('fs__center-marker');
    this.content.classList.add('fs__advanced-marker');
    this.content.classList.add('fs__advanced-feature-marker');
  }

  private _addIconLabel(): void {
    if (!this.iconLabel) return;

    const labelElement = document.createElement('span');
    labelElement.textContent = this.iconLabel.text;
    labelElement.classList.add('fs__marker-icon-label');

    // Apply positioning and styling
    if (this.iconLabel.top !== undefined) {
      labelElement.style.top = `${this.iconLabel.top}px`;
    }
    if (this.iconLabel.left !== undefined) {
      labelElement.style.left = `${this.iconLabel.left}px`;
    }
    if (this.iconLabel.color) {
      labelElement.style.color = this.iconLabel.color;
    }
    if (this.iconLabel.fontSize) {
      labelElement.style.fontSize = this.iconLabel.fontSize;
    }
    if (this.iconLabel.transform) {
      labelElement.style.transform = this.iconLabel.transform;
    }

    this.content.appendChild(labelElement);
  }

  private _updateZIndexByPriority(): void {
    const priorityZIndex = {
      high: 1000,
      normal: 500,
      low: 100,
    };

    const baseZIndex = priorityZIndex[this.getPriority()];
    this.setZIndex(baseZIndex + (this.getZIndex() % 100));
  }
}
